/**
 * Preferences Management
 * Ported from: innovation-claude/poc/mfe-shared/src/user_preferences.rs + session_preferences.rs
 *
 * Two types of preferences:
 * - UserPreferences: Persistent (localStorage)
 * - SessionPreferences: Session-scoped (sessionStorage)
 *
 * This module also provides PreferencesOps for managing app-wide preferences
 * (notifications, confirmations, hotkeys, etc.)
 */

import { StorageEngine } from '@innolab-trader/storage'
import type { UserPreferences, SessionPreferences } from './types'
import type { PreferencesData, NotificationFlags, HotkeyCommands } from './preferences-types'

// Namespace storage keys by stage/env to avoid cross-environment collisions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const STAGE = (import.meta as any)?.env?.VITE_STAGE || 'local'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ENV = (import.meta as any)?.env?.VITE_ENV || 'demo'
const ns = (base: string) => `${STAGE}:${ENV}:${base}`
const USER_PREFS_KEY = ns('user-preferences')
const SESSION_PREFS_KEY = ns('session-preferences')
// Non-namespaced theme key shared with index.html bootstrap
const THEME_KEY = 'innolab-trader-theme'

/**
 * Helper: Get value from storage with default
 * Uses StorageEngine.local() for proper abstraction
 */
export async function getFromStorage<T>(key: string, defaultValue: T): Promise<T> {
  try {
    const doc = StorageEngine.local().document<T>(key)
    const stored = await doc.load()
    return stored ?? defaultValue
  } catch {
    return defaultValue
  }
}

/**
 * Helper: Set value in storage
 * Uses StorageEngine.local() for proper abstraction
 */
export async function setInStorage(key: string, value: unknown): Promise<void> {
  try {
    const doc = StorageEngine.local().document(key)
    await doc.save(value)
  } catch (error) {
    console.error('Failed to save to storage:', error)
  }
}

/**
 * Preferences Manager
 * Handles loading and saving user and session preferences
 */
export class PreferencesManager {
    /**
     * Load user preferences from storage
     * Uses StorageEngine.local() for localStorage
     *
     * @param engine - LocalStorage engine
     * @returns User preferences or defaults
     */
    static async loadUserPrefs(engine: StorageEngine): Promise<UserPreferences> {
        const doc = engine.document<UserPreferences>(USER_PREFS_KEY)
        const loaded = await doc.load()

        const defaults: UserPreferences = { locale: 'en', theme: 'system' }
        const prefs: UserPreferences = loaded ? { ...defaults, ...loaded } : defaults

        // If a non-namespaced theme override exists, apply it
        try {
            const themeDoc = engine.document<string>(THEME_KEY)
            const theme = await themeDoc.load()
            if (theme === 'dark' || theme === 'light') {
                prefs.theme = theme
            }
        } catch {}

        return prefs
    }

    /**
     * Save user preferences to storage
     *
     * @param engine - LocalStorage engine
     * @param prefs - Preferences to save
     */
    static async saveUserPrefs(engine: StorageEngine, prefs: UserPreferences): Promise<void> {
        const doc = engine.document<UserPreferences>(USER_PREFS_KEY)
        await doc.save(prefs)

        // Keep the shared theme key in sync for fast bootstrap
        try {
            if (prefs.theme === 'dark' || prefs.theme === 'light') {
                await engine.document<string>(THEME_KEY).save(prefs.theme)
            }
        } catch {}
    }

    /**
     * Update locale and persist
     *
     * @param engine - LocalStorage engine
     * @param locale - New locale code
     */
    static async setLocale(engine: StorageEngine, locale: string): Promise<void> {
        const prefs = await this.loadUserPrefs(engine)
        prefs.locale = locale
        await this.saveUserPrefs(engine, prefs)
    }

    /**
     * Update theme and persist
     *
     * @param engine - LocalStorage engine
     * @param theme - New theme
     */
    static async setTheme(engine: StorageEngine, theme: 'light' | 'dark' | 'system'): Promise<void> {
        const prefs = await this.loadUserPrefs(engine)
        prefs.theme = theme
        await this.saveUserPrefs(engine, prefs)
        // Also write through to the non-namespaced key for shell bootstrap
        try {
            if (theme === 'dark' || theme === 'light') {
                await engine.document<string>(THEME_KEY).save(theme)
            }
        } catch {}
    }

    /**
     * Load session preferences from storage
     * Uses StorageEngine.session() for sessionStorage
     *
     * @param engine - SessionStorage engine
     * @returns Session preferences or defaults
     */
    static async loadSessionPrefs(engine: StorageEngine): Promise<SessionPreferences> {
        const doc = engine.document<SessionPreferences>(SESSION_PREFS_KEY)
        const loaded = await doc.load()

        // Return loaded prefs or empty object (tradingEnvironment will be undefined)
        return loaded || {}
    }

    /**
     * Save session preferences to storage
     *
     * @param engine - SessionStorage engine
     * @param prefs - Preferences to save
     */
    static async saveSessionPrefs(engine: StorageEngine, prefs: SessionPreferences): Promise<void> {
        const doc = engine.document<SessionPreferences>(SESSION_PREFS_KEY)
        await doc.save(prefs)
    }

    /**
     * Update trading environment and persist
     *
     * @param engine - SessionStorage engine
     * @param environment - New trading environment
     */
    static async setTradingEnvironment(
        engine: StorageEngine,
        environment: 'demo' | 'live' | 'replay'
    ): Promise<void> {
        const prefs = await this.loadSessionPrefs(engine)
        prefs.tradingEnvironment = environment
        await this.saveSessionPrefs(engine, prefs)
    }

    /**
     * Reset session preferences to defaults
     * Clears sessionStorage and sets trading environment to 'demo'
     * Called when user lands on /welcome page
     *
     * @param engine - SessionStorage engine
     */
    static async resetSessionPrefs(engine: StorageEngine): Promise<void> {
        try {
            // Clear session storage by deleting the session preferences document
            const doc = engine.document<SessionPreferences>(SESSION_PREFS_KEY)
            await doc.delete()

            // Do NOT set a default tradingEnvironment here.
            // Leaving it undefined ensures the trading-mode page is shown after login
            // and prevents an immediate redirect to /workspace.
        } catch (error) {
            console.error('[PreferencesManager] Failed to reset session preferences:', error)
            throw error
        }
    }
}

/**
 * Sync preferences after authentication
 *
 * Called by Stores (WelcomeVM, etc.) after successful login to:
 * 1. Load user preferences from localStorage (or remote if available)
 * 2. Return theme and locale for UI layer to apply
 *
 * This centralizes preference loading logic so it's:
 * - Reusable across all MFEs
 * - Testable (unit test the function)
 * - Explicit (VM calls it after auth, not reactive component)
 * - Business logic stays in VM layer, not React components
 *
 * @returns User preferences with fallback defaults
 */
export async function syncPreferencesAfterAuth(): Promise<{
    locale: string
    theme: string
}> {
    try {
        console.log('[syncPreferencesAfterAuth] Loading user preferences...')

        // Load from localStorage
        const storage = StorageEngine.local()
        const prefs = await PreferencesManager.loadUserPrefs(storage)

        const result = {
            locale: prefs?.locale || 'en',
            theme: prefs?.theme || 'dark',
        }

        console.log('[syncPreferencesAfterAuth] Loaded preferences:', result)
        return result
    } catch (error) {
        console.error('[syncPreferencesAfterAuth] Failed to load preferences:', error)

        // Return safe defaults on error
        return {
            locale: 'en',
            theme: 'dark',
        }
    }
}

/**
 * Preferences API Operations
 *
 * Provides centralized access to user preferences (notifications, confirmations, hotkeys, etc.)
 * Used by mfe-management and other MFEs that need to read/write preferences.
 *
 * Phase 1: Uses localStorage via helper functions
 * Phase 2: Will migrate to Transport + backend /user/preferences endpoint
 */
export const PreferencesOps = {
  /**
   * Load all user preferences
   * Returns complete preferences object
   */
  async getPreferences(): Promise<Partial<PreferencesData>> {
    try {
      // Load theme via PreferencesManager (source of truth)
      const userPrefs = await PreferencesManager.loadUserPrefs(StorageEngine.local())
      const theme = (userPrefs?.theme === 'system' ? 'auto' : userPrefs?.theme) || 'dark'

      return {
        theme: theme,
        ticker: await getFromStorage('notifications.ticker', { enabled: false, timeZone: 'EST' }),
        summaryEmail: await getFromStorage('user.summaryEmail', false),
        newsNotifications: await getFromStorage('notifications.news.enabled', false),
        orderNotifications: await getFromStorage('notifications.order', {}) as NotificationFlags,
        pushNotifications: await getFromStorage('notifications.push', {}) as NotificationFlags,
        audioNotifications: await getFromStorage('notifications.audio', {}) as NotificationFlags,
        confirmations: {
          orders: await getFromStorage('user.confirmations.enabled', true),
          closeModule: await getFromStorage('user.closeModuleConfirmation', true),
          alerts: await getFromStorage('user.alertSettings.modal', true),
        },
        autoRollContracts: await getFromStorage('auto.roll.contracts', false),
        thirdPartyDataSharing: await getFromStorage('user.thirdPartyDataSharing', false),
        hotkeys: {
          enabled: await getFromStorage('user.hotkeys.enabled', false),
          commands: await getFromStorage('user.hotkeys.commands', {}) as HotkeyCommands,
        },
      }
    } catch (error) {
      console.error('Failed to load preferences:', error)
      throw error
    }
  },

  /**
   * Update user preferences
   * Batch update multiple settings at once
   */
  async updatePreferences(updates: Partial<PreferencesData>): Promise<void> {
    if (updates.theme !== undefined) {
      const theme = updates.theme === 'auto' ? 'system' : updates.theme
      await PreferencesManager.setTheme(StorageEngine.local(), theme)
    }

    if (updates.ticker !== undefined) {
      await setInStorage('notifications.ticker', updates.ticker)
    }

    // density removed (no-op)

    if (updates.summaryEmail !== undefined) {
      await setInStorage('user.summaryEmail', updates.summaryEmail)
    }

    if (updates.newsNotifications !== undefined) {
      await setInStorage('notifications.news.enabled', updates.newsNotifications)
    }

    if (updates.orderNotifications !== undefined) {
      await setInStorage('notifications.order', updates.orderNotifications)
    }

    if (updates.pushNotifications !== undefined) {
      await setInStorage('notifications.push', updates.pushNotifications)
    }

    if (updates.audioNotifications !== undefined) {
      await setInStorage('notifications.audio', updates.audioNotifications)
    }

    if (updates.confirmations !== undefined) {
      await setInStorage('user.confirmations.enabled', updates.confirmations.orders)
      await setInStorage('user.closeModuleConfirmation', updates.confirmations.closeModule)
      await setInStorage('user.alertSettings.modal', updates.confirmations.alerts)
    }

    if (updates.autoRollContracts !== undefined) {
      await setInStorage('auto.roll.contracts', updates.autoRollContracts)
    }

    if (updates.thirdPartyDataSharing !== undefined) {
      await setInStorage('user.thirdPartyDataSharing', updates.thirdPartyDataSharing)
    }

    if (updates.hotkeys !== undefined) {
      await setInStorage('user.hotkeys.enabled', updates.hotkeys.enabled)
      await setInStorage('user.hotkeys.commands', updates.hotkeys.commands)
    }
  },
}
